import { convertFahrenheitToCelsius } from "./common_utils";
import {
  CONNECTION_TYPE,
  CONNECTION_TYPE_DISPLAY_VALUES,
  CONTROL_SOURCE,
  CURRENT_LIMIT_DISPLAY_VALUES,
  OP_MODE,
  OP_MODE_DISPLAY_VALUES,
} from "./constants";

export const SORT_DIRECTION = {
  ASC: "ASC",
  DESC: "DESC",
};

export const ORDER_OPTIONS = [
  { label: "Ascending (↑)", value: SORT_DIRECTION.ASC },
  { label: "Descending (↓)", value: SORT_DIRECTION.DESC },
];

export const ZONE_UNIT_SORT_OPTIONS = [
  { label: "Unit Name", value: "name", columnKey: "name" },
  { label: "Serial Number", value: "serialNumber", columnKey: "serialNumber" },
  { label: "Alerts", value: "alerts", columnKey: "alerts" },
  { label: "Connection", value: "connected" },
  { label: "Power", value: "running" },
  { label: "Mode", value: "mode" },
  { label: "Indoor Temperature", value: "indoorTemp", columnKey: "indoorTemperature" },
  { label: "Setpoint", value: "setpoint", columnKey: "setpoint" },
  { label: "Current Limit", value: "currentLimit", columnKey: "currentLimit" },
  { label: "Connection Type", value: "connectionType" },
  { label: "Control Source", value: "controlSource", columnKey: "controlSource" },
  { label: "Schedule Source", value: "scheduleSource", columnKey: "scheduleSource" },
];

export const ALL_UNIT_SORT_OPTIONS = [
  { label: "Unit Name", value: "name" },
  { label: "Serial Number", value: "serialNumber" },
  { label: "Activation", value: "activated" },
  { label: "Zone", value: "belongsToZone" },
  { label: "Configuration", value: "configured" },
];

const ALL_OPTION = { label: "All", value: "" };

const selectSection = (key, title, options) => ({
  key,
  title,
  options: [ALL_OPTION, ...options],
});

const rangeSection = (title, minKey, maxKey) => ({
  key: minKey,
  title,
  type: "range",
  minKey,
  maxKey,
});

const optionsFromDisplayValues = (displayValues) =>
  Object.entries(displayValues).map(([value, label]) => ({ label, value }));

export const ZONE_UNIT_FILTER_SECTIONS = [
  selectSection("hasAlerts", "Alerts", [
    { label: "Has Alerts", value: "true" },
    { label: "No Alerts", value: "false" },
  ]),
  selectSection("connected", "Connection", [
    { label: "Connected", value: "true" },
    { label: "Disconnected", value: "false" },
  ]),
  selectSection("running", "Power", [
    { label: "Powered On", value: "true" },
    { label: "Powered Off", value: "false" },
  ]),
  selectSection("mode", "Mode", [
    { label: OP_MODE_DISPLAY_VALUES[OP_MODE.HEAT], value: OP_MODE.HEAT },
    { label: OP_MODE_DISPLAY_VALUES[OP_MODE.COOL], value: OP_MODE.COOL },
    { label: OP_MODE_DISPLAY_VALUES[OP_MODE.HEAT_AND_COOL], value: OP_MODE.HEAT_AND_COOL },
    { label: OP_MODE_DISPLAY_VALUES[OP_MODE.DRY], value: OP_MODE.DRY },
    { label: OP_MODE_DISPLAY_VALUES[OP_MODE.FAN_ONLY], value: OP_MODE.FAN_ONLY },
  ]),
  rangeSection("Indoor Temperature (°F)", "minIndoorTempF", "maxIndoorTempF"),
  rangeSection("Setpoint (°F)", "minSetpointF", "maxSetpointF"),
  selectSection("currentLimit", "Current Limit", optionsFromDisplayValues(CURRENT_LIMIT_DISPLAY_VALUES)),
  selectSection("connectionType", "Connection Type", [
    { label: CONNECTION_TYPE_DISPLAY_VALUES[CONNECTION_TYPE.WIFI], value: CONNECTION_TYPE.WIFI },
    { label: CONNECTION_TYPE_DISPLAY_VALUES[CONNECTION_TYPE.LTE], value: CONNECTION_TYPE.LTE },
  ]),
  selectSection("controlSource", "Control Source", [
    { label: "Controlled by Nexus", value: CONTROL_SOURCE.NEXUS },
    { label: "Controlled by User", value: CONTROL_SOURCE.USER },
    { label: "Not Applied", value: CONTROL_SOURCE.NOT_APPLIED },
  ]),
  selectSection("scheduleSource", "Schedule Source", [
    { label: "Scheduled by Nexus", value: CONTROL_SOURCE.NEXUS },
    { label: "Scheduled by User", value: CONTROL_SOURCE.USER },
    { label: "Not Applied", value: CONTROL_SOURCE.NOT_APPLIED },
  ]),
];

export const ALL_UNIT_FILTER_SECTIONS = [
  selectSection("activated", "Activation", [
    { label: "Active", value: "true" },
    { label: "Inactive", value: "false" },
  ]),
  selectSection("belongsToZone", "Zone", [
    { label: "In Zone", value: "true" },
    { label: "Not In Zone", value: "false" },
  ]),
  selectSection("configured", "Configuration", [
    { label: "Configured", value: "true" },
    { label: "Not Configured", value: "false" },
  ]),
];

export const filterSectionKeys = (section) =>
  section.type === "range" ? [section.minKey, section.maxKey] : [section.key];

export const emptyFilters = (sections) =>
  sections.reduce(
    (filters, section) =>
      filterSectionKeys(section).reduce((carry, key) => ({ ...carry, [key]: "" }), filters),
    {}
  );

export const hasActiveFilters = (filters) => Object.values(filters || {}).some((value) => value !== "" && value != null);

const FAHRENHEIT_DISPLAY_ROUNDING_HALF = 0.5;

const fahrenheitFilterToCelsius = (value, offsetF) => {
  if (value === "" || value == null) {
    return undefined;
  }
  const fahrenheit = Number(value);
  return Number.isFinite(fahrenheit) ? convertFahrenheitToCelsius(fahrenheit + offsetF) : undefined;
};

const minFahrenheitFilterToCelsius = (value) => fahrenheitFilterToCelsius(value, -FAHRENHEIT_DISPLAY_ROUNDING_HALF);

const maxFahrenheitFilterToCelsius = (value) => fahrenheitFilterToCelsius(value, FAHRENHEIT_DISPLAY_ROUNDING_HALF);

export const toZoneUnitApiFilters = (filters = {}) => ({
  currentLimit: filters.currentLimit || undefined,
  hasAlerts: filters.hasAlerts || undefined,
  controlSource: filters.controlSource || undefined,
  scheduleSource: filters.scheduleSource || undefined,
  connected: filters.connected || undefined,
  connectionType: filters.connectionType || undefined,
  running: filters.running || undefined,
  mode: filters.mode || undefined,
  minIndoorTempC: minFahrenheitFilterToCelsius(filters.minIndoorTempF),
  maxIndoorTempC: maxFahrenheitFilterToCelsius(filters.maxIndoorTempF),
  minSetpointC: minFahrenheitFilterToCelsius(filters.minSetpointF),
  maxSetpointC: maxFahrenheitFilterToCelsius(filters.maxSetpointF),
});

export const toAllUnitApiFilters = (filters = {}) => ({
  activated: filters.activated || undefined,
  belongsToZone: filters.belongsToZone || undefined,
  configured: filters.configured || undefined,
});

export const sortByToColumnKey = ZONE_UNIT_SORT_OPTIONS.reduce(
  (map, { value, columnKey }) => (columnKey ? { ...map, [value]: columnKey } : map),
  {}
);

export const columnKeyToSortBy = Object.fromEntries(
  Object.entries(sortByToColumnKey).map(([sortBy, columnKey]) => [columnKey, sortBy])
);

export const ZONE_UNIT_LIST_QUERY = {
  sortOptions: ZONE_UNIT_SORT_OPTIONS,
  filterSections: ZONE_UNIT_FILTER_SECTIONS,
};

export const ALL_UNIT_LIST_QUERY = {
  sortOptions: ALL_UNIT_SORT_OPTIONS,
  filterSections: ALL_UNIT_FILTER_SECTIONS,
};

export const FIRST_PAGE = 1;

const PAGE_PARAM = "page";
const SEARCH_PARAM = "search";
const SORT_BY_PARAM = "sortBy";
const SORT_DIRECTION_PARAM = "sortDirection";

const readAllowedValue = (params, key, allowedValues) => {
  const value = params.get(key);
  return allowedValues.includes(value) ? value : "";
};

const readRangeBound = (params, key) => {
  const value = params.get(key);
  if (value === null || value.trim() === "") {
    return "";
  }
  return Number.isFinite(Number(value)) ? value : "";
};

const readPage = (params) => {
  const page = Number(params.get(PAGE_PARAM));
  return Number.isInteger(page) && page >= FIRST_PAGE ? page : FIRST_PAGE;
};

/**
 * The last page that still holds results, so a page number that came from a
 * stale or shared url can be brought back into range
 *
 */
export const lastPageForCount = (count, pageSize) =>
  Math.max(FIRST_PAGE, Math.ceil((count || 0) / pageSize));

/**
 * Read the search, sort, filter and page state a unit list should show from its
 * url, dropping any value the matching endpoint would reject
 *
 */
export const readUnitListQuery = (urlSearch, { sortOptions, filterSections }) => {
  const params = new URLSearchParams(urlSearch);

  return {
    page: readPage(params),
    search: params.get(SEARCH_PARAM) || "",
    sortBy: readAllowedValue(params, SORT_BY_PARAM, sortOptions.map(({ value }) => value)),
    sortDirection: readAllowedValue(params, SORT_DIRECTION_PARAM, ORDER_OPTIONS.map(({ value }) => value)),
    filters: filterSections.reduce((filters, section) => {
      if (section.type === "range") {
        return {
          ...filters,
          [section.minKey]: readRangeBound(params, section.minKey),
          [section.maxKey]: readRangeBound(params, section.maxKey),
        };
      }
      const allowedValues = section.options
        .map(({ value }) => value)
        .filter((value) => value !== "");
      return { ...filters, [section.key]: readAllowedValue(params, section.key, allowedValues) };
    }, {}),
  };
};

/**
 * Write search, sort, filter and page state into a url search string, leaving
 * any query parameter the unit list does not own untouched
 *
 */
export const writeUnitListQuery = (urlSearch, { filterSections }, { page, search, sortBy, sortDirection, filters }) => {
  const params = new URLSearchParams(urlSearch);

  const setOrDelete = (key, value) => {
    if (value === "" || value == null) {
      params.delete(key);
      return;
    }
    params.set(key, String(value));
  };

  setOrDelete(PAGE_PARAM, page > FIRST_PAGE ? page : "");
  setOrDelete(SEARCH_PARAM, search);
  setOrDelete(SORT_BY_PARAM, sortBy);
  setOrDelete(SORT_DIRECTION_PARAM, sortDirection);
  filterSections.forEach((section) => {
    filterSectionKeys(section).forEach((key) => setOrDelete(key, filters?.[key]));
  });

  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : "";
};
